import React, {
  FunctionComponent,
  useCallback,
} from 'react'
import {
  Text,
  View,
  Pressable,
  ScrollView,
  TextInput,
  Switch,
  TouchableOpacity,
  Modal,
  Platform,
} from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons'
import DateTimePicker from '../../components/DateTimePickerWrapper'

import { DocumentData, NouvelleDemandeProps } from './NouvelleDemande.types'
import useNouvelleDemandeStyle from './NouvelleDemande.styles'
import { useNouvelleDemandeState } from './NouvelleDemande.states'
import { DocumentPicker, LottieWrapper } from '../../components'
import { CalendrierUniversel } from '../../components/CalendrierUniversel'

import {
  switchStyle,
  stdCouleurGris,
  stdCouleurBleuClair,
  stdCouleurBleu,
} from '@commonStyle'

import dayjs from 'dayjs'
import { useNotify } from '../../components/Notification'
import useEnTetePage from '../../components/EnTetePage/EnTetePage'

export const NouvelleDemande: FunctionComponent<NouvelleDemandeProps> = ({
  navigation,
  session: _session,
  route,
}) => {
  const notify = useNotify()
  const { styles } = useNouvelleDemandeStyle()
  const insets = useSafeAreaInsets()

  const {
    // États
    markedDates,
    maxDate,
    absence,
    unenatureAbsence,
    dotation,
    naturesAbsences,
    visibleDotation,
    showCalendar,
    displayText,
    hourToChange,

    // Actions
    setAbsence,
    handleNatureAbsence,
    toggleApresMidi,
    togglePrevision,
    toggleMatin,
    closeCalendar,
    handleFilePicked,
    handleFileError,
    handleDroit,
    handleAnnule,
    handleValide,
    handleTimeChange,
    loadNaturesAbsencesIfNeeded,
    handleDateSelection,
    openCalendar,
    openHourPicker,

    // États pour le modal
    modalVisible,
    setModalVisible,
    isLoadingNatures,

    // Documents
    selectedDocument,
    documentInfo,
    isUploadingDocument,
    documentError,
    handleDocumentSelect,
    handleDocumentRemove,
    handleDocumentDownload,
  } = useNouvelleDemandeState(navigation, route, notify)

  const { EnTete } = useEnTetePage({text: displayText});

  // Ouverture de la modale
  const handleNatureAbsenceClick = useCallback(async () => {
    try {
      const data = await loadNaturesAbsencesIfNeeded()

      if (data && data.length > 0) {
        setModalVisible(true)
      }
    } catch (error) {
      console.error(
        "❌ Erreur lors du chargement des natures d'absence:",
        error,
      )
    }
  }, [loadNaturesAbsencesIfNeeded, setModalVisible])

  // Fermer la modale (prévenir freeze iOS)
  const closeModalSafely = useCallback(() => {
    setModalVisible(false)

    if (Platform.OS === 'ios') {
      requestAnimationFrame(() => {
        // éventuel setState si besoin
      })
    }
  }, [setModalVisible])

  const renderCustomHeader = (date: Date) => {
    const monthYear = dayjs(date)
      .format('MMMM YYYY')
      .replace(/^\w/, (c) => c.toUpperCase())
    return (
      <View style={styles.customHeaderContainer}>
        <Text style={styles.customHeaderText}>{monthYear}</Text>
      </View>
    )
  }

  // --------- Rendu NORMAL (sans modale) ---------

  const renderNatureAbsence = () => {
    return (
      <View style={styles.card}>
        <View style={styles.natureAbsenceContainer}>
          <Pressable
            onPress={handleNatureAbsenceClick}
            style={[
              styles.natureAbsenceButton,
              (absence.statut === -1 || absence.statut === 1) &&
                styles.disabled,
            ]}
            disabled={absence.statut === -1 || absence.statut === 1}
            testID="BT_CHOIX_NATURE"
          >
            <Text
              style={
                unenatureAbsence
                  ? styles.natureAbsenceText
                  : styles.natureAbsencePlaceholder
              }
            >
              {unenatureAbsence
                ? unenatureAbsence.label
                : "Nature d'absence"}
            </Text>
            <MaterialCommunityIcons
              name="chevron-down"
              size={25}
              color="#6b7280"
            />
          </Pressable>

          {dotation?.droit && (
            <Pressable
              onPress={handleDroit}
              style={styles.droitsButton}
              testID="BT_SHOW_DOTATIONS"
            >
              <MaterialCommunityIcons
                name="gauge-empty"
                size={25}
                color="#3b82f6"
              />
            </Pressable>
          )}
        </View>

        {visibleDotation === 1 && dotation?.droit && (
          <>
            <View style={styles.dotationHeader}>
              <View style={styles.dotationItem}>
                <Text style={styles.dotationValue}>
                  {dotation.droit || 0}
                </Text>
                <Text style={styles.dotationLabel}>Droit</Text>
              </View>
              <View style={styles.dotationItem}>
                <Text style={styles.dotationValue}>
                  {dotation.pris || 0}
                </Text>
                <Text style={styles.dotationLabel}>Pris</Text>
              </View>
              <View style={styles.dotationItem}>
                <Text style={styles.dotationValue}>
                  {dotation.solde || 0}
                </Text>
                <Text style={styles.dotationLabel}>Solde actuel</Text>
              </View>

              <View style={styles.dotationItem}>
                <Text style={styles.dotationValue}>
                  {dotation.pris_a_venir || 0}
                </Text>
                <Text style={styles.dotationLabel}>À venir</Text>
              </View>
              <View style={styles.dotationItem}>
                <Text style={styles.dotationValue}>
                  {dotation.pris_a_valider || 0}
                </Text>
                <Text style={styles.dotationLabel}>Attente</Text>
              </View>
            </View>
            <View style={styles.dotationReste}>
              <Text style={styles.dotationResteLabel}>Reste : </Text>
              <Text style={styles.dotationResteValue}>
                {dotation.reste || 0}
              </Text>
            </View>
          </>
        )}
      </View>
    )
  }

  const renderDate = (
    date: dayjs.Dayjs | null,
    placeholder: string,
    testID: string,
  ) => {
    return (
      <TouchableOpacity
        onPress={openCalendar}
        style={[styles.dateButton, styles.dateCardButton]}
        testID={testID}
      >
        {date ? (
          <Text style={styles.dateButtonText}>
            {date.format('dddd')} {date.format('DD/MM/YYYY')}
          </Text>
        ) : (
          <Text style={styles.dateButtonText}>{placeholder}</Text>
        )}
      </TouchableOpacity>
    )
  }

  const renderDates = () => {
    if (absence.dateDebut || showCalendar) {
      return (
        <View style={styles.cardContentContainer}>
          {renderDate(absence.dateDebut, 'Du', 'BT_DATE_DU')}
          <MaterialCommunityIcons
            name="arrow-right-bold"
            size={30}
            color="#3b82f6"
          />
          {renderDate(absence.dateFin, 'Au', 'BT_DATE_AU')}
        </View>
      )
    } else {
      return (
        <Pressable
          onPress={() => {
            openCalendar()
          }}
          testID="BT_CHOIX_DATE"
        >
          <View style={styles.datePlaceholder}>
            <Text style={styles.datePlaceholderText}>
              Choisir une date
            </Text>
          </View>
        </Pressable>
      )
    }
  }

  const renderCalendrier = () => {
    if (!showCalendar) return null
    return (
      <View style={styles.calendarContainer}>
        <CalendrierUniversel
          onDayPress={handleDateSelection}
          markedDates={markedDates}
          maxDate={maxDate}
          renderHeader={(date) => renderCustomHeader(date)}
          withSubHeader={false}
          mode="month"
        />
        <TouchableOpacity
          onPress={closeCalendar}
          style={styles.calendarValidateButton}
          testID="BT_VALIDATION_DATE"
        >
          <Text style={styles.calendarValidateText}>
            Valider la date
          </Text>
        </TouchableOpacity>
      </View>
    )
  }

  const renderHeure = (date: dayjs.Dayjs, testID: string) => {
    return (
      <View style={styles.dateCardButton}>
        <TouchableOpacity
          onPress={openHourPicker(date)}
          testID={testID}
        >
          <Text style={styles.heureText}>
            {date ? date.format('HH:mm') : '00:00'}
          </Text>
        </TouchableOpacity>
      </View>
    )
  }

  const renderDefinitionHeures = () => {
    if (absence.dateDebut && absence.dateFin && absence.natureAbsence) {
      return (
        <>
          <View style={styles.cardContentContainer}>
            {renderHeure(
              absence.heureDebut || absence.dateDebut,
              'BT_HORAIRE_DEB',
            )}
            <View style={styles.dateCardRemplissage} />
            {renderHeure(
              absence.heureFin || absence.dateFin,
              'BT_HORAIRE_FIN',
            )}
          </View>
          {renderPlageRapide()}
          <Text style={styles.dureeText}>{absence.duree}</Text>
        </>
      )
    }
  }

  const renderPlageRapideItem = (
    label: string,
    value: boolean,
    onValueChange: () => void,
    testID: string,
  ) => {
    if (absence.typeProfil === 2) return null
    return (
      <View style={styles.dateCardButton}>
        <Switch
          trackColor={{
            false: stdCouleurGris,
            true: stdCouleurBleuClair,
          }}
          thumbColor={stdCouleurBleu}
          ios_backgroundColor={stdCouleurGris}
          onValueChange={onValueChange}
          value={value}
          disabled={absence.statut === -1 || absence.statut === 1}
          style={[styles.switchStyle, switchStyle]}
          testID={testID}
        />
        <Text style={styles.plageText}>{label}</Text>
      </View>
    )
  }

  const renderPlageRapide = () => {
    if (showCalendar || !absence.dateDebut || !absence.dateFin || !absence.natureAbsence) {
      return null
    }

    return (
      <View style={styles.cardContentContainer}>
        {!(
          absence.dateDebut.isSame(absence.dateFin, 'day') &&
          absence.matin
        ) &&
          renderPlageRapideItem(
            "Dès l'après-midi",
            absence.apresMidi,
            toggleApresMidi,
            'INT_APREM',
          )}
        {!(
          absence.dateDebut.isSame(absence.dateFin, 'day') &&
          (absence.apresMidi || absence.matin)
        ) && <View style={styles.dateCardRemplissage} />}
        {!(
          absence.dateDebut.isSame(absence.dateFin, 'day') &&
          absence.apresMidi
        ) &&
          renderPlageRapideItem(
            'Uniquement le Matin',
            absence.matin,
            toggleMatin,
            'INT_MATIN',
          )}
      </View>
    )
  }

  const renderPrevision = () => {
    if (showCalendar || !absence.dateDebut || !absence.dateFin || !absence.natureAbsence) {
      return null
    }

    return (
      <View style={styles.card}>
        <View style={styles.previsionContainer}>
          <Switch
            trackColor={{
              false: stdCouleurGris,
              true: stdCouleurBleuClair,
            }}
            thumbColor={stdCouleurBleu}
            ios_backgroundColor={stdCouleurGris}
            onValueChange={togglePrevision}
            value={absence.prevision}
            disabled={absence.statut === -1 || absence.statut === 1}
            style={[styles.switchStyle, switchStyle]}
            testID="INT_PREVISION"
          />
          <Text>Enregistrer comme prévision</Text>
        </View>
      </View>
    )
  }

  const renderFichier = () => {
    if (showCalendar || !absence.dateDebut || !absence.dateFin || !absence.natureAbsence) {
      return null
    }

    if ((absence.statut === -1 || absence.statut === 1) && !documentInfo) {
      return null
    }

    const hasDocument = selectedDocument || documentInfo
    const isDocumentReadOnly = absence.statut === -1 || absence.statut === 1

    return (
      <View style={styles.card}>
        <View style={styles.fichierContainer}>
          {documentInfo && !selectedDocument && (
            <View style={styles.documentInfoContainer}>
              <View style={styles.documentInfoContent}>
                <MaterialCommunityIcons
                  name="file-document"
                  size={25}
                  color="#3b82f6"
                />
                <View style={styles.documentInfoText}>
                  <Text style={styles.documentFileName}>
                    {documentInfo.filename ||
                      documentInfo.libelle ||
                      JSON.stringify(documentInfo)}
                  </Text>
                  <Text style={styles.documentFileInfo}>
                    {documentInfo.size &&
                      `${Math.round(documentInfo.size / 1024)} KB`}
                    {documentInfo.uploadDate &&
                      ` • ${dayjs(documentInfo.uploadDate).format(
                        'DD/MM/YYYY',
                      )}`}
                  </Text>
                </View>
              </View>
              <View style={styles.documentActions}>
                <Pressable
                  onPress={() =>
                    handleDocumentDownload(
                      absence.identifiant,
                      documentInfo,
                    )
                  }
                  style={styles.documentActionButton}
                  disabled={isUploadingDocument}
                  testID="BT_DOWNLOAD_PJ"
                >
                  <MaterialCommunityIcons
                    name="download"
                    size={20}
                    color={isUploadingDocument ? '#9ca3af' : '#3b82f6'}
                  />
                </Pressable>
                {!isDocumentReadOnly && (
                  <Pressable
                    onPress={handleDocumentRemove}
                    style={styles.documentActionButton}
                    testID="BT_DELETE_PJ"
                  >
                    <MaterialCommunityIcons
                      name="delete"
                      size={20}
                      color="#ef4444"
                    />
                  </Pressable>
                )}
              </View>
            </View>
          )}

          {selectedDocument && (
            <View style={styles.documentInfoContainer}>
              <View style={styles.documentInfoContent}>
                <MaterialCommunityIcons
                  name="file-plus"
                  size={25}
                  color="#10b981"
                />
                <View style={styles.documentInfoText}>
                  <Text style={styles.documentFileName}>
                    {selectedDocument.filename || 'Nouveau document'}
                  </Text>
                  <Text style={styles.documentFileInfo}>
                    {selectedDocument.size &&
                      `${Math.round(
                        selectedDocument.size / 1024,
                      )} KB • Nouveau`}
                  </Text>
                </View>
              </View>
              <Pressable
                onPress={handleDocumentRemove}
                style={styles.documentActionButton}
                testID="BT_DELETE_PJ"
              >
                <MaterialCommunityIcons
                  name="close"
                  size={20}
                  color="#6b7280"
                />
              </Pressable>
            </View>
          )}

          {!hasDocument && !isDocumentReadOnly && (
            <DocumentPicker
              onFilePicked={(result) => {
                console.log('Fichier sélectionné:', result)
                if (result.uri && result.name) {
                  const documentData: DocumentData = {
                    file: {
                      uri: result.uri,
                      name: result.name,
                      type:
                        result.type || 'application/octet-stream',
                    },
                    filename: result.name,
                    mimetype:
                      result.type || 'application/octet-stream',
                    size: result.size || 0,
                  }
                  handleDocumentSelect(documentData)
                }
                handleFilePicked(result)
              }}
              onError={handleFileError}
              acceptedTypes={['*/*']}
              multiple={false}
              testID="BT_SELECTION_PJ"
            >
              <View style={styles.fichierButton}>
                <MaterialCommunityIcons
                  name="paperclip"
                  size={25}
                  color="#3b82f6"
                />
                <Text style={styles.fichierText}>
                  Ajouter une pièce jointe
                </Text>
              </View>
            </DocumentPicker>
          )}

          {documentError && (
            <View style={styles.documentErrorContainer}>
              <Text style={styles.documentErrorText}>
                {documentError}
              </Text>
            </View>
          )}
        </View>
      </View>
    )
  }

  const renderCommentaire = () => {
    if (showCalendar || !absence.dateDebut || !absence.dateFin || !absence.natureAbsence) {
      return null
    }

    if ((absence.statut === -1 || absence.statut === 1) && !absence.commentaire) {
      return null
    }

    return (
      <View style={styles.card}>
        <TextInput
          style={styles.commentaireInput}
          placeholder={
            absence.statut !== -1 ? 'Ajouter un commentaire' : ''
          }
          placeholderTextColor="#9ca3af"
          autoCapitalize="none"
          onChangeText={(value) =>
            setAbsence((prev) => ({ ...prev, commentaire: value }))
          }
          value={absence.commentaire}
          multiline
          numberOfLines={4}
          editable={
            absence.statut !== -1 && absence.statut !== 1
          }
          testID="SAI_COMMENTAIRE"
        />
      </View>
    )
  }

  const renderBoutons = () => {
    if (showCalendar || !absence.dateDebut || !absence.dateFin || !absence.natureAbsence) {
      return null
    }

    if (absence.statut === -1) {
      return null
    }

    const titleValide =
      absence.statut !== 2 ? 'Modifier' : 'Valider'
    const titleAnnule =
      absence.statut === 2 ? 'Annuler' : 'Supprimer'
    const isDisabled =
      !absence.duree || absence.duree.split(' ')[0] === '0'

    return (
      <View style={styles.buttonsContainer}>
        <View style={styles.buttonsRow}>
          {absence.statut !== 1 && (
            <>
              <Pressable
                disabled={isDisabled}
                style={[
                  styles.buttonPrimary,
                  isDisabled && styles.buttonPrimaryDisabled,
                ]}
                onPress={handleValide}
                testID="BT_VALIDATION"
              >
                <Text style={styles.buttonText}>
                  {titleValide}
                </Text>
              </Pressable>
              <Pressable
                style={styles.buttonSecondary}
                onPress={handleAnnule}
                testID="BT_ANNULATION"
              >
                <Text style={styles.buttonText}>
                  {titleAnnule}
                </Text>
              </Pressable>
            </>
          )}
        </View>
      </View>
    )
  }

  const renderSelectHeure = () => {
    if (absence.dateDebut && absence.dateFin && absence.natureAbsence) {
      if (hourToChange) {
        let defaultValue = new Date()
        if (absence.heureDebut?.isSame(hourToChange)) {
          defaultValue = absence.heureDebut.toDate()
        } else if (absence.heureFin?.isSame(hourToChange)) {
          defaultValue = absence.heureFin.toDate()
        }

        return (
          <DateTimePicker
            value={defaultValue}
            onChange={handleTimeChange(hourToChange)}
            mode="time"
            is24Hour
            display="default"
          />
        )
      }
    }
  }

  const renderAnimation = () => {
    if (absence.natureAbsence && (showCalendar || absence.dateDebut)) {
      return null
    }

    return (
      <View style={styles.animationContainer}>
        <LottieWrapper
          source={require('../../assets/lotties/absence.json')}
          autoPlay
          loop
          style={styles.lottie}
        />
      </View>
    )
  }

  const renderCardDatesHeures = () => {
    if (!absence.natureAbsence) return null

    return (
      <View style={styles.card}>
        {renderDates()}
        {renderDefinitionHeures()}
        {renderCalendrier()}
      </View>
    )
  }

  // --------- Rendu MODALE SEULE ---------

  const renderModalNaturesAbsences = () => {
    const handleSelectNature = (item: any) => {
      // 1) appliquer la sélection d'abord
      handleNatureAbsence(item)
      // 2) fermer la modale et ouvrir le calendrier
      setTimeout(() => {
        closeModalSafely()
        openCalendar()
      }, 0)
    }

    return (
      <View style={{ flex: 1 }}>
        <Modal
          visible={modalVisible}
          animationType="slide"
          transparent={false}
          onRequestClose={closeModalSafely}
        >
          <View style={[styles.modalContainer, { paddingTop: insets.top }]}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>
                Nature d'absence ({naturesAbsences.length})
              </Text>
              <Pressable
                onPress={closeModalSafely}
                testID="BT_EXIT_MODAL"
              >
                <MaterialCommunityIcons
                  name="close"
                  size={24}
                  color="#666"
                />
              </Pressable>
            </View>

            <ScrollView>
              {naturesAbsences.map((item) => (
                <Pressable
                  key={item.value.toString()}
                  onPress={() => handleSelectNature(item)}
                  style={styles.modalItem}
                  testID={`BT_${item.code
                    .toUpperCase()
                    .replaceAll(' ', '_')}`}
                >
                  <View style={styles.modalItemContent}>
                    <Text style={styles.modalItemText}>
                      {item.label}
                    </Text>
                    {item.code && (
                      <Text style={styles.modalItemBadge}>
                        {item.code}
                      </Text>
                    )}
                  </View>
                </Pressable>
              ))}

              {!naturesAbsences.length && (
                <View style={styles.emptyListContainer}>
                  <Text style={styles.emptyListText}>
                    {isLoadingNatures
                      ? 'Chargement...'
                      : "Aucune nature d'absence disponible"}
                  </Text>
                </View>
              )}
            </ScrollView>
          </View>
        </Modal>
      </View>
    )
  }

  // --------- Rendu principal (une seule page) ---------

  if (modalVisible) {
    // Quand la modale est ouverte, on ne rend QUE la modale
    return renderModalNaturesAbsences()
  }

  return (
    <View style={styles.container}>
      {EnTete}
      <ScrollView
        contentContainerStyle={styles.scrollContainer}
        testID="SC_MAIN_CONTENT"
      >
        <View style={styles.mainContent}>
          {renderNatureAbsence()}
          {renderCardDatesHeures()}
          {renderPrevision()}
          {renderFichier()}
          {renderCommentaire()}
        </View>
      </ScrollView>
      {renderBoutons()}
      {renderAnimation()}
      {renderSelectHeure()}
    </View>
  )
}
